import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { TierProgramCreateInput, TierProgramRow } from "@colab/shared";

type CreateProgramResponse = { id: string; program: TierProgramRow };

// POST /api/admin/tiers/programs
//
// Pessimistic — program creation is low-frequency and the new row is
// surfaced via invalidation. Setting `is_default: true` here transactionally
// clears any prior default in the service, so the partners list must also
// refetch to reflect the new resolved default tier on unassigned partners.
export function useCreateTierProgram() {
  const queryClient = useQueryClient();
  return useMutation<CreateProgramResponse, ApiClientError, TierProgramCreateInput>({
    mutationFn: (input) =>
      apiFetch<CreateProgramResponse>("/admin/tiers/programs", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.programs.list() });
      if (vars.is_default) {
        queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to create tier program");
    },
  });
}
